import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../services/authService';
import { format } from 'date-fns';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [filters, setFilters] = useState({ status: '', role: '' });
  const [editModal, setEditModal] = useState({ open: false, user: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, user: null });
  const [actionLoading, setActionLoading] = useState(null);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.role) params.role = filters.role;

      const data = await adminService.getUsers(params);
      setUsers(Array.isArray(data) ? data : data.results || []);
    } catch {
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleUpdateUser = async (id, data) => {
    setActionLoading(id);
    setError(null);
    try {
      await adminService.updateUser(id, data);
      setSuccessMessage('User updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchUsers();
      setEditModal({ open: false, user: null });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const toggleStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'pending' : 'active';
    await handleUpdateUser(user.id, { status: newStatus });
  };

  const toggleEnabled = async (user) => {
    await handleUpdateUser(user.id, { enabled: !user.enabled });
  };

  const handleDeleteUser = async (user) => {
    setActionLoading(user.id);
    setError(null);
    try {
      await adminService.deleteUser(user.id);
      setSuccessMessage(`User ${user.email} permanently deleted`);
      setTimeout(() => setSuccessMessage(null), 3000);
      fetchUsers();
      setDeleteModal({ open: false, user: null });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-primary-500/20 text-primary-400',
      owner: 'bg-purple-500/20 text-purple-400',
      member: 'bg-dark-700 text-dark-400',
    };
    const displayRole = role || 'member';
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[displayRole] || styles.member}`}>
        {displayRole.charAt(0).toUpperCase() + displayRole.slice(1)}
      </span>
    );
  };

  const getStatusBadge = (status, enabled) => {
    if (!enabled) {
      return (
        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-error-500/20 text-error-400">
          Disabled
        </span>
      );
    }
    const styles = {
      active: 'bg-success-500/20 text-success-400',
      pending: 'bg-warning-500/20 text-warning-400',
    };
    const displayStatus = status || 'pending';
    return (
      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${styles[displayStatus] || styles.pending}`}>
        {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-dark-100">User Management</h1>
        <p className="text-dark-400 mt-1">Manage platform users and their permissions</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })}
          className="input-dark"
        >
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
        </select>
        <select
          value={filters.role}
          onChange={(e) => setFilters({ ...filters, role: e.target.value })}
          className="input-dark"
        >
          <option value="">All Roles</option>
          <option value="admin">Admin</option>
          <option value="member">Member</option>
        </select>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="bg-success-500/10 border border-success-500/30 rounded-lg p-4">
          <p className="text-success-400">{successMessage}</p>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-error-500/10 border border-error-500/30 rounded-lg p-4">
          <p className="text-error-400">{error}</p>
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="card-dark p-12 text-center">
          <svg
            className="w-16 h-16 text-dark-600 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
          <p className="text-dark-400">No users found</p>
        </div>
      ) : (
        <div className="card-dark overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  User
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Joined
                </th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Last Login
                </th>
                <th className="text-right px-6 py-4 text-xs font-semibold text-dark-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-dark-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 bg-primary-500/20 rounded-full flex items-center justify-center mr-3">
                        <span className="text-sm font-medium text-primary-400">
                          {user.first_name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-dark-100 font-medium">
                          {user.first_name} {user.last_name}
                        </p>
                        <p className="text-dark-500 text-sm">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">{getRoleBadge(user.role)}</td>
                  <td className="px-6 py-4">
                    {getStatusBadge(user.status, user.enabled)}
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-dark-400 text-sm">
                      {user.created_at
                        ? format(new Date(user.created_at), 'MMM d, yyyy')
                        : '-'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-dark-400 text-sm">
                      {user.last_login
                        ? format(new Date(user.last_login), 'MMM d, yyyy HH:mm')
                        : 'Never'}
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      {user.enabled && (
                        <button
                          onClick={() => toggleStatus(user)}
                          disabled={actionLoading === user.id}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                            user.status === 'active'
                              ? 'bg-warning-500/20 text-warning-400 hover:bg-warning-500/30'
                              : 'bg-success-500/20 text-success-400 hover:bg-success-500/30'
                          }`}
                        >
                          {user.status === 'active' ? 'Set Pending' : 'Activate'}
                        </button>
                      )}
                      <button
                        onClick={() => toggleEnabled(user)}
                        disabled={actionLoading === user.id}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                          user.enabled
                            ? 'bg-error-500/20 text-error-400 hover:bg-error-500/30'
                            : 'bg-success-500/20 text-success-400 hover:bg-success-500/30'
                        }`}
                      >
                        {user.enabled ? 'Disable' : 'Enable'}
                      </button>
                      {!user.enabled && (
                        <button
                          onClick={() => setDeleteModal({ open: true, user })}
                          disabled={actionLoading === user.id}
                          className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
                        >
                          Remove
                        </button>
                      )}
                      <button
                        onClick={() => setEditModal({ open: true, user: { ...user } })}
                        className="px-3 py-1.5 bg-dark-600 text-dark-300 rounded-lg text-sm font-medium hover:bg-dark-500 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editModal.open && editModal.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-dark-100 mb-4">Edit User</h3>
            <div className="space-y-4">
              <div>
                <p className="text-dark-400 text-sm mb-1">Email</p>
                <p className="text-dark-100">{editModal.user.email}</p>
              </div>
              <div>
                <p className="text-dark-400 text-sm mb-1">Name</p>
                <p className="text-dark-100">
                  {editModal.user.first_name} {editModal.user.last_name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">Role</label>
                <select
                  value={editModal.user.role || 'member'}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      user: { ...editModal.user, role: e.target.value },
                    })
                  }
                  className="input-dark w-full"
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-dark-300 mb-2">Status</label>
                <select
                  value={editModal.user.status || 'pending'}
                  onChange={(e) =>
                    setEditModal({
                      ...editModal,
                      user: { ...editModal.user, status: e.target.value },
                    })
                  }
                  className="input-dark w-full"
                >
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-dark-300">Account Enabled</span>
                <button
                  type="button"
                  onClick={() =>
                    setEditModal({
                      ...editModal,
                      user: { ...editModal.user, enabled: !editModal.user.enabled },
                    })
                  }
                  className={`w-12 h-6 rounded-full transition-colors ${
                    editModal.user.enabled ? 'bg-success-500' : 'bg-dark-600'
                  }`}
                >
                  <div
                    className={`w-5 h-5 bg-white rounded-full transition-transform ${
                      editModal.user.enabled ? 'translate-x-6' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setEditModal({ open: false, user: null })}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  handleUpdateUser(editModal.user.id, {
                    role: editModal.user.role,
                    status: editModal.user.status,
                    enabled: editModal.user.enabled,
                  })
                }
                disabled={actionLoading === editModal.user.id}
                className="btn-primary"
              >
                {actionLoading === editModal.user.id ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.open && deleteModal.user && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card-dark p-6 max-w-md w-full">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-dark-100">Delete User Permanently</h3>
                <p className="text-dark-400 text-sm">This action cannot be undone</p>
              </div>
            </div>
            <div className="bg-dark-900 rounded-lg p-4 mb-4">
              <p className="text-dark-300 text-sm mb-2">You are about to permanently delete:</p>
              <p className="text-dark-100 font-medium">{deleteModal.user.email}</p>
              <p className="text-dark-400 text-sm">{deleteModal.user.first_name} {deleteModal.user.last_name}</p>
            </div>
            <p className="text-dark-400 text-sm mb-6">
              This will remove the user from Cognito and all database records.
              The user will be able to register again as a new user.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteModal({ open: false, user: null })}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteUser(deleteModal.user)}
                disabled={actionLoading === deleteModal.user.id}
                className="px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === deleteModal.user.id ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
